import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { TableShell } from '../../components/molecules/TableShell'
import { Paginator } from '../../components/molecules/Paginator'
import { OrganisationFormModal } from './OrganisationFormModal'
import { useOrganisations, type OrganisationDto } from '../../../viewmodels/useOrganisations'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/organisations — operator-managed catalog of organisations
 * with their postal addresses (HQ + branches). Full CRUD via the form
 * modal; the row "Addresses" column summarises the count + the first
 * one for at-a-glance scanning.
 */
export function OrganisationsPage(): JSX.Element {
  const vm = useOrganisations()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.MANAGEORGANISATIONS, 'add')
  const canEdit = can(SCREENCODES.MANAGEORGANISATIONS, 'edit')
  const canDelete = can(SCREENCODES.MANAGEORGANISATIONS, 'delete')

  const [modalMode, setModalMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [editTarget, setEditTarget] = useState<OrganisationDto | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const openCreate = (): void => {
    setEditTarget(null)
    setModalMode('create')
  }
  const openEdit = (row: OrganisationDto): void => {
    setEditTarget(row)
    setModalMode('edit')
  }
  const closeModal = (): void => setModalMode('closed')

  const handleDelete = async (row: OrganisationDto): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm(`Delete organisation "${row.name}"?`)) return
    setDeletingId(row.id)
    setDeleteError(null)
    const res = await vm.remove(row.id)
    setDeletingId(null)
    if (!res.ok) setDeleteError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Organisations</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            Each organisation has a name, optional company URL + description, and one or
            more postal addresses (HQ + branches).
          </p>
        </div>
        {canAdd ? <Button onClick={openCreate}>Add</Button> : null}
      </header>

      {deleteError !== null ? <Alert tone="error">{deleteError}</Alert> : null}
      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => vm.reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <Table
            items={vm.items}
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={openEdit}
            onDelete={(row) => void handleDelete(row)}
            deletingId={deletingId}
          />
          <Paginator
            page={vm.page}
            pageSize={vm.pageSize}
            total={vm.total}
            onPageChange={vm.setPage}
            disabled={vm.isLoading}
          />
        </>
      )}

      <OrganisationFormModal
        open={modalMode !== 'closed'}
        mode={modalMode === 'edit' ? 'edit' : 'create'}
        initial={editTarget}
        onClose={closeModal}
        onSubmit={async (input) => {
          if (modalMode === 'edit' && editTarget !== null) {
            return vm.update(editTarget.id, input)
          }
          return vm.create(input as Parameters<typeof vm.create>[0])
        }}
      />
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No organisations yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to create the first
        organisation.
      </p>
    </div>
  )
}

function Table({
  items,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  deletingId
}: {
  readonly items: OrganisationDto[]
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly onEdit: (row: OrganisationDto) => void
  readonly onDelete: (row: OrganisationDto) => void
  readonly deletingId: string | null
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Name</Th>
            <Th>Company URL</Th>
            <Th>Addresses</Th>
            <Th>Description</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const first = row.addresses[0]
            const summary = first === undefined
              ? '—'
              : `${first.label} · ${[first.line1, first.city].filter(Boolean).join(', ')}`
            return (
              <tr key={row.id} className="border-t border-border">
                <Td>{row.name}</Td>
                <Td className="max-w-[18rem] truncate">
                  {row.company_url === null ? (
                    <span className="text-text-secondary">—</span>
                  ) : (
                    <a
                      href={row.company_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-clay underline underline-offset-2 hover:text-charcoal"
                    >
                      {row.company_url}
                    </a>
                  )}
                </Td>
                <Td>
                  <span title={summary}>
                    {row.addresses.length === 0 ? (
                      <span className="text-text-secondary">—</span>
                    ) : (
                      <>
                        <span className="inline-flex items-center rounded-full border border-border bg-parchment/60 px-2 py-0.5 font-body text-body-xs text-text-secondary mr-2">
                          {row.addresses.length}
                        </span>
                        <span className="text-text-secondary">{summary}</span>
                      </>
                    )}
                  </span>
                </Td>
                <Td
                  className="max-w-[20rem] truncate"
                  title={row.description ?? ''}
                >
                  {row.description ?? <span className="text-text-secondary">—</span>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
                        Edit
                      </Button>{' '}
                    </>
                  ) : null}
                  {canDelete && !row.is_master ? (
                    // Master org cannot be deleted (Kiran 2026-06-08).
                    // Backend rejects with 409 too — hiding the button
                    // is just UX so the operator doesn't think they
                    // can.
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(row)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}
